import React, {useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography} from "@mui/material";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import {useNavigate} from "react-router-dom";
import Bottom from "../Bottom";
import Top from "../Top";
import LineChart from "../../components/LineChart";
import GaugeChart from "../../components/GaugeChart";
import DeviceReadingList from "../../components/DeviceReadingList";
import DeviceNormalRange from "../../components/DeviceNormalRange";
import pressureIcon from "../../assets/icon/pressure.gif";
import * as util from "../../utils/util";
import {
  DeviceInfo,
  getDeviceMetrics,
  getPressureStatus
} from "../../utils/deviceInfo";
import {
  latestOf,
  latestReadingOf,
  pressureTrendOf,
  readingsOf,
  useHealthDevice,
} from "../../utils/healthDeviceApi";

const TREND_DAY_COUNT = 7;

export const PRESSURE_COLOR = { systolic: '#800d8d', diastolic: '#3dbb27' };

const HistoryDevicePressureDetail = (props: { device: DeviceInfo }) => {
  const device = props.device;
  const navigate = useNavigate();
  const [dayValue, setDayValue] = useState(0);

  const targetDate = util.getDateByCustom('yyyyMMdd', dayValue);
  const metrics = getDeviceMetrics(device.id);

  // AP_009_004 — 그날 기록(today)과 일자별 요약(daily)이 한 번에 온다
  const { data: deviceData } = useHealthDevice(dayValue);
  const trend = pressureTrendOf(deviceData, TREND_DAY_COUNT, dayValue);

  // 하루에 여러 번 기록 가능 - 최상단 / 그래프는 마지막(최신) 기록 기준
  const readings = readingsOf(deviceData, device);
  const latestReading = latestReadingOf(deviceData, device);
  const latest = latestOf(deviceData, device, dayValue);

  const targetPressure = { bloodPressureHigh: latest.systolic, bloodPressureLow: latest.diastolic };
  const status = getPressureStatus(latest.systolic, latest.diastolic);

  // 최근 7일 (오늘 포함) 날짜 라벨
  const trendLabels: string[] = [];
  for (let index = 0; index < TREND_DAY_COUNT; index++) {
    const date = util.getDateByCustom('yyyyMMdd', dayValue + index - (TREND_DAY_COUNT - 1));
    trendLabels.push(date.slice(4, 6) + '.' + date.slice(6, 8));
  }

  const series = [
    { curve: 'linear', data: trend.diastolic, color: PRESSURE_COLOR.diastolic },
    { curve: 'linear', data: trend.systolic, color: PRESSURE_COLOR.systolic },
  ];
  const xAxis = [{ scaleType: 'point', data: trendLabels }];

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title={device.typeName + ' 기록'} />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>

          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setDayValue((prev: any) => prev - 1)} sx={{ verticalAlign: 'text-bottom' }} />
            {targetDate.slice(0, 4) + '.' + targetDate.slice(4, 6) + '.' + targetDate.slice(6, 8)}
            <ArrowRightIcon onClick={() => setDayValue((prev: any) => prev + 1)} sx={{ 'display': dayValue < 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          {/* 요약 */}
          <Box sx={{ m: '20px 0', bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: '10px 15px 8px 15px' }}>
              <Typography sx={{ fontSize: 14, color: '#333', fontWeight: 700 }}>{device.name}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: device.excluded ? '#bbb' : '#59b544', border: '1px solid', borderColor: device.excluded ? '#e0e0e0' : '#a5dd97', borderRadius: '10px', p: '2px 8px' }}>
                {device.excluded ? '실증 제외' : '연동됨'}
              </Typography>
            </Stack>
            <Divider />

            <Stack direction={'row'} justifyContent={'center'} sx={{ p: 1 }}>
              <Grid>
                <GaugeChart targetValue={util.getPercentValueForGauge(targetPressure)} />
                <Typography component={'div'} sx={{ width: '100%', textAlign: 'center', color: status.color, fontSize: 16, fontWeight: 700, mb: 1 }}>{status.text}</Typography>
              </Grid>

              <Stack justifyContent={'center'} sx={{ m: 'auto' }}>
                <Avatar alt="" src={pressureIcon} sx={{ m: 'auto', width: '60px', height: '60px', borderRadius: 0 }} />
              </Stack>

              <Stack direction={'row'} alignItems={'center'} sx={{ ml: 'auto', mr: 2 }}>
                <Stack direction={'column'} sx={{ ml: 'auto' }}>
                  <Typography component="div" sx={{ fontSize: 30, color: status.color, fontWeight: 700, textAlign: 'center' }}>
                    {latest.systolic + '/' + latest.diastolic}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 18, color: '#777', mb: 0, textAlign: 'center' }}>
                    mmHg
                  </Typography>
                  {/* 하루 여러 건 중 마지막(최신) 기록임을 알 수 있도록 측정시간 노출 */}
                  {latestReading === undefined ? null : (
                    <Typography component="span" sx={{ fontSize: 12, color: '#999', textAlign: 'center' }}>
                      {latestReading.time} 기준
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {/* 혈압 기록 */}
          <Accordion defaultExpanded sx={{ borderRadius: '15px', position: 'static', mb: '20px', '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="device-pressure-content"
              id="device-pressure-header"
              sx={{
                '&#device-pressure-header': { minHeight: '55px' },
                '& .MuiAccordionSummary-content.Mui-expanded': { m: 0 }
              }}
            >
              <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>혈압 기록</Typography>
              <Typography sx={{ fontSize: 12, color: '#888', ml: 1, alignSelf: 'center' }}>{readings.length}건</Typography>
            </AccordionSummary>
            <Divider />

            <AccordionDetails>
              {/* 하루 동안 기록된 측정값 전체 (측정시간 + 측정값) */}
              <DeviceReadingList readings={readings} metrics={metrics} />
            </AccordionDetails>
          </Accordion>

          {/* 최근 7일 추이 */}
          <Box sx={{ bgcolor: '#fff', p: '0 10px 10px 10px', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack>
                {/* 하루 여러 건 중 마지막(최신) 기록을 그날 값으로 사용 */}
                <Typography component="div" sx={{ fontSize: 14, color: '#555' }}>최근 7일</Typography>
                <Typography component="div" sx={{ fontSize: 11, color: '#999' }}>일별 최신 기록 기준</Typography>
              </Stack>
              <Stack sx={{ ml: 'auto', zIndex: 2 }}>
                <Button sx={{ fontSize: 12, color: '#555' }} endIcon={<ArrowRightIcon />}
                  onClick={() => { navigate('../history/HistoryDeviceMonthHistory', { state: { deviceId: device.id } }) }}>
                  월간현황
                </Button>
              </Stack>
            </Stack>
            <Divider />

            <Box sx={{ mt: -3.5 }}>
              <Box sx={{
                '& .MuiLineElement-root': { strokeWidth: 0 },
                '& .MuiMarkElement-series-auto-generated-id-0': { strokeWidth: 0, fill: PRESSURE_COLOR.diastolic },
                '& .MuiMarkElement-series-auto-generated-id-1': { strokeWidth: 0, fill: PRESSURE_COLOR.systolic },
              }}>
                <LineChart series={series} xAxis={xAxis} isReferenceLine={false} />
              </Box>
              <PressureLegend />
            </Box>
          </Box>

          <DeviceNormalRange deviceType={device.type} />
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

// 차트 범례 (수축기 / 이완기)
export const PressureLegend = () => (
  <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} mt={'-10px'} mb={1}>
    <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} mr={1}>
      <Box sx={{ width: 10, height: 10, bgcolor: PRESSURE_COLOR.systolic, mr: .5, borderRadius: 3 }}></Box>
      <Typography sx={{ fontSize: 12, color: '#777' }}>수축기</Typography>
    </Stack>
    <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} mr={1}>
      <Box sx={{ width: 10, height: 10, bgcolor: PRESSURE_COLOR.diastolic, mr: .5, borderRadius: 3 }}></Box>
      <Typography sx={{ fontSize: 12, color: '#777' }}>이완기</Typography>
    </Stack>
  </Stack>
);

const CardShadow = '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)';

export default HistoryDevicePressureDetail;
